/**
 * Canonical session-scope helpers for portal shell selection.
 * Prefer server /auth/me fields over stale localStorage role guesses.
 */

import { isSelfRepairUser, isAgencyUser, isPlatformAdmin, isManagedClientUser } from './portalAccess';
import { getApiBaseUrl } from './authUtils';
import { resolveAuthenticatedHome } from './authenticatedHome';

export const AUTH_BOOTSTRAP_STATES = {
  PENDING: 'pending',
  READY: 'ready',
  UNAUTHENTICATED: 'unauthenticated',
  ERROR: 'error',
};

export function resolveSessionPortal(user) {
  if (!user) return null;
  return String(
    user.portal
    || user.active_account?.portal
    || user.selected_account?.portal
    || user.portal_type
    || ''
  ).toLowerCase() || null;
}

export function resolveSessionAccountType(user) {
  if (!user) return null;
  return String(
    user.account_type
    || user.active_account?.type
    || user.selected_account?.account_type
    || user.active_account?.account_type
    || ''
  ).toLowerCase() || null;
}

export function resolveSessionRole(user) {
  if (!user) return null;
  return String(
    user.effective_role
    || user.active_account?.effective_role
    || user.active_account?.role
    || user.role
    || ''
  ).toLowerCase() || null;
}

/** True when server-scoped session is personal self-repair (never agency shell). */
export function isScopedSelfRepairSession(user) {
  if (!user) return false;
  const portal = resolveSessionPortal(user);
  const accountType = resolveSessionAccountType(user);
  if (portal === 'self_repair' || accountType === 'self_repair') return true;
  if (portal === 'self_serve_portal' || user.portal_type === 'self_serve_portal') {
    if (!isAgencyUser(user) && !isPlatformAdmin(user)) return true;
  }
  return isSelfRepairUser(user) && !isAgencyUser(user);
}

export function isScopedAgencySession(user) {
  if (!user || isScopedSelfRepairSession(user)) return false;
  return isAgencyUser(user);
}

/**
 * Detect contradictory scoped session fields (preview fail-closed helper).
 * Example: portal=self_repair but agency routing fields still authoritative.
 */
export function detectContradictorySessionScope(user) {
  if (!user || user.requires_account_selection || user.pending_account_selection) {
    return null;
  }
  const portal = resolveSessionPortal(user);
  const accountType = resolveSessionAccountType(user);
  const role = resolveSessionRole(user);
  const agencyId = user.agency_id || null;
  const selfRepairish =
    portal === 'self_repair'
    || accountType === 'self_repair'
    || ['self_repair', 'self_repair_customer', 'self_repair_user'].includes(role);
  const agencyish =
    portal === 'agency'
    || portal === 'agency_staff'
    || accountType === 'agency'
    || ['agency_owner', 'agency_admin', 'agency_staff', 'owner', 'admin', 'staff'].includes(role);

  if (selfRepairish && agencyish && portal && accountType && portal !== accountType.replace('agency_staff', 'agency')) {
    // portal vs account_type conflict between self_repair and agency
    if (
      (portal === 'self_repair' && accountType === 'agency')
      || (portal === 'agency' && accountType === 'self_repair')
    ) {
      return {
        reason: 'portal_account_type_mismatch',
        portal,
        accountType,
        role,
        agency_id: agencyId,
      };
    }
  }

  if (
    (portal === 'self_repair' || accountType === 'self_repair')
    && agencyId
    && isAgencyUser(user)
  ) {
    return {
      reason: 'self_repair_classified_as_agency',
      portal,
      accountType,
      role,
      agency_id: agencyId,
    };
  }

  if (
    selfRepairish
    && !portal
    && !accountType
    && agencyId
    && isAgencyUser(user)
  ) {
    return {
      reason: 'role_self_repair_with_agency_routing',
      portal,
      accountType,
      role,
      agency_id: agencyId,
    };
  }

  return null;
}

export function canCallAgencyStaffApis(user) {
  return isScopedAgencySession(user) || isPlatformAdmin(user);
}

export function canCallOwnerConsoleApis(user) {
  return isPlatformAdmin(user);
}

export function homePathForScopedSession(user) {
  if (!user) return '/login';
  if (user.requires_account_selection || user.pending_account_selection) {
    return '/choose-account';
  }
  return resolveAuthenticatedHome({ user, portal: resolveSessionPortal(user) });
}

/**
 * Preview / production API targeting.
 * Vercel Preview must set REACT_APP_BACKEND_URL to the preview API host —
 * never inherit production api.fundelite.ai for preview deploys.
 */
export function getConfiguredBackendUrl() {
  return getApiBaseUrl();
}

export function isPreviewFrontendHost(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const host = String(hostname || '').toLowerCase();
  return host.includes('vercel.app') || host.startsWith('api-preview.');
}

export function isProductionApiUrl(url = getConfiguredBackendUrl()) {
  try {
    const host = new URL(url.includes('://') ? url : `https://${url}`).hostname;
    return host === 'api.fundelite.ai' || host === 'www.api.fundelite.ai';
  } catch {
    return String(url || '').includes('api.fundelite.ai');
  }
}

export function previewFrontendUsesProductionApi({
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  backendUrl = getConfiguredBackendUrl(),
} = {}) {
  return isPreviewFrontendHost(hostname) && isProductionApiUrl(backendUrl);
}

export function assertSafePreviewApiTarget(options = {}) {
  if (!previewFrontendUsesProductionApi(options)) return { ok: true };
  return {
    ok: false,
    reason: 'Preview frontend must use REACT_APP_BACKEND_URL pointing at the preview API (e.g. https://api-preview.fundelite.ai), not https://api.fundelite.ai',
  };
}
